/**
 * Metric definitions and formatting utilities
 * @module MetricDataDevTool/utils/metricDefinitions
 */

/**
 * Metric metadata defining display properties and formatting rules
 * @constant {Object.<string, {displayName: string, type: string, decimals: number}>}
 */
export const METRIC_DEFINITIONS = {
  bohOvertimeDollars: {
    displayName: 'BOH Overtime Dollars',
    type: 'currency',
    decimals: 2
  },
  bohOvertimeHours: {
    displayName: 'BOH Overtime Hours',
    type: 'hours',
    decimals: 1
  },
  bohRegularDollars: {
    displayName: 'BOH Regular Dollars',
    type: 'currency',
    decimals: 2
  },
  bohRegularHours: {
    displayName: 'BOH Regular Hours',
    type: 'hours',
    decimals: 1
  },
  checkCount: {
    displayName: 'Check Count',
    type: 'count',
    decimals: 0
  },
  fohOvertimeDollars: {
    displayName: 'FOH Overtime Dollars',
    type: 'currency',
    decimals: 2
  },
  fohOvertimeHours: {
    displayName: 'FOH Overtime Hours',
    type: 'hours',
    decimals: 1
  },
  fohRegularDollars: {
    displayName: 'FOH Regular Dollars',
    type: 'currency',
    decimals: 2
  },
  fohRegularHours: {
    displayName: 'FOH Regular Hours',
    type: 'hours',
    decimals: 1
  },
  giftCardSales: {
    displayName: 'Gift Card Sales',
    type: 'currency',
    decimals: 2
  },
  grossSales: {
    displayName: 'Gross Sales',
    type: 'currency',
    decimals: 2
  },
  netSales: {
    displayName: 'Net Sales',
    type: 'currency',
    decimals: 2
  },
  refunds: {
    displayName: 'Refunds',
    type: 'currency',
    decimals: 2
  },
  serviceCharges: {
    displayName: 'Service Charges',
    type: 'currency',
    decimals: 2
  }
};

/**
 * Get display name for a metric
 * @param {string} metricName - The metric name
 * @returns {string} Display name or original name if not found
 */
export function getMetricDisplayName(metricName) {
  return METRIC_DEFINITIONS[metricName]?.displayName || metricName;
}

/**
 * Get metric type (currency, hours, count)
 * @param {string} metricName - The metric name
 * @returns {string} Metric type, defaults to 'count'
 */
export function getMetricType(metricName) {
  return METRIC_DEFINITIONS[metricName]?.type || 'count';
}

/**
 * Get decimal places for metric formatting
 * @param {string} metricName - The metric name
 * @returns {number} Number of decimal places, defaults to 0
 */
export function getMetricFormat(metricName) {
  return METRIC_DEFINITIONS[metricName]?.decimals ?? 0;
}

/**
 * Format a metric value according to its type
 * @param {string} metricName - The metric name
 * @param {number|null|undefined} value - The value to format
 * @returns {string} Formatted value
 */
export function formatMetricValue(metricName, value) {
  const numValue = value ?? 0;
  const decimals = getMetricFormat(metricName);

  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

/**
 * Get all metric names in order
 * @returns {string[]} Array of all metric names
 */
export function getAllMetricNames() {
  return Object.keys(METRIC_DEFINITIONS);
}
